import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Input } from '@agms/ui'
import api, { leadApi, ticketApi } from '../../lib/api'
import type { Lead, PaginatedResponse, Ticket, TicketCategory, TicketPriority, TicketSlaType } from '../../lib/api'
import { apiErrorMessage } from '../../hooks/useErrorHandler'

interface MerchantOption {
  id: string
  dba_name: string
  fiserv_mid: string | null
}

interface EditTicketModalProps {
  ticket: Ticket
  onClose: () => void
  onUpdated: (ticket: Ticket) => void
}

export function EditTicketModal({ ticket, onClose, onUpdated }: EditTicketModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const [subject, setSubject] = useState(ticket.subject)
  const [body, setBody] = useState(ticket.body ?? '')
  const [priority, setPriority] = useState<TicketPriority>(ticket.priority)
  const [category, setCategory] = useState<TicketCategory>(ticket.category)
  const [slaType, setSlaType] = useState<TicketSlaType>(ticket.sla_type ?? '1_day')
  const [slaCustomDays, setSlaCustomDays] = useState(ticket.sla_custom_days ?? 14)
  const [linkMode, setLinkMode] = useState<'merchant' | 'lead'>(ticket.merchant_id ? 'merchant' : 'lead')
  const [search, setSearch] = useState('')
  const [merchantId, setMerchantId] = useState(ticket.merchant_id ?? '')
  const [leadId, setLeadId] = useState(ticket.lead_id ?? '')
  const [error, setError] = useState<string | null>(null)

  const { data: merchantOptions } = useQuery({
    queryKey: ['ticket-edit-merchant-picker', search],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<MerchantOption>>('/merchants', {
        params: { search: search.trim() || undefined, per_page: 25 },
      })
      return response.data.data
    },
    enabled: linkMode === 'merchant',
  })

  const { data: leadOptions } = useQuery({
    queryKey: ['ticket-edit-lead-picker', search],
    queryFn: async () => {
      const response = await leadApi.list({ search: search.trim() || undefined, per_page: 25 })
      return response.data
    },
    enabled: linkMode === 'lead',
  })

  const updateMutation = useMutation({
    mutationFn: () => ticketApi.update(ticket.id, {
      subject: subject.trim(),
      body: body.trim() || null,
      priority,
      category,
      sla_type: slaType,
      sla_custom_days: slaType === 'custom' ? slaCustomDays : null,
      merchant_id: linkMode === 'merchant' ? merchantId : null,
      lead_id: linkMode === 'lead' ? leadId : null,
    }),
    onSuccess: (updated) => {
      onUpdated(updated)
      onClose()
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not update ticket.'))
    },
  })

  const eomPreview = (() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  })()

  const canSubmit = subject.trim().length > 0
    && (linkMode === 'merchant' ? merchantId.length > 0 : leadId.length > 0)
    && (slaType !== 'custom' || (slaCustomDays >= 1 && slaCustomDays <= 45))
    && !updateMutation.isPending

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const firstControl = dialogRef.current?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])')
    firstControl?.focus()

    return () => {
      previousFocus?.focus()
    }
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <div
        ref={dialogRef}
        className="w-full max-w-2xl rounded-lg bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-ticket-title"
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 id="edit-ticket-title" className="text-lg font-semibold text-gray-900">Edit Ticket</h2>
        </div>

        <div className="space-y-4 px-5 py-4">
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {error}
            </div>
          )}

          <Input
            label="Subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            maxLength={500}
          />

          <label className="text-sm font-medium text-gray-700">
            Description
            <textarea
              className="mt-1 block min-h-[120px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          </label>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Priority
              <select
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={priority}
                onChange={(event) => setPriority(event.target.value as TicketPriority)}
              >
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700">
              Category
              <select
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={category}
                onChange={(event) => setCategory(event.target.value as TicketCategory)}
              >
                <option value="billing">Billing</option>
                <option value="technical">Technical</option>
                <option value="onboarding">Onboarding</option>
                <option value="general">General</option>
              </select>
            </label>

          </div>

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="text-sm font-medium text-gray-700">Linked Account</div>
            <div className="mt-2 flex gap-2">
              <Button
                type="button"
                variant={linkMode === 'merchant' ? 'primary' : 'secondary'}
                onClick={() => setLinkMode('merchant')}
              >
                Merchant
              </Button>
              <Button
                type="button"
                variant={linkMode === 'lead' ? 'primary' : 'secondary'}
                onClick={() => setLinkMode('lead')}
              >
                Lead
              </Button>
            </div>
            <Input
              label={`Search ${linkMode === 'merchant' ? 'merchants' : 'leads'}`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by DBA, legal name, email, or MID"
            />
            {linkMode === 'merchant' ? (
              <select
                aria-label="Select linked merchant"
                className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={merchantId}
                onChange={(event) => setMerchantId(event.target.value)}
              >
                <option value="">Select merchant...</option>
                {ticket.merchant && (
                  <option value={ticket.merchant.id}>
                    {ticket.merchant.dba_name} {ticket.merchant.fiserv_mid ? `- MID ${ticket.merchant.fiserv_mid}` : ''}
                  </option>
                )}
                {(merchantOptions ?? [])
                  .filter((merchant) => merchant.id !== ticket.merchant?.id)
                  .map((merchant) => (
                    <option key={merchant.id} value={merchant.id}>
                      {merchant.dba_name} {merchant.fiserv_mid ? `- MID ${merchant.fiserv_mid}` : ''}
                    </option>
                  ))}
              </select>
            ) : (
              <select
                aria-label="Select linked lead"
                className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={leadId}
                onChange={(event) => setLeadId(event.target.value)}
              >
                <option value="">Select lead...</option>
                {ticket.lead && (
                  <option value={ticket.lead.id}>
                    {ticket.lead.dba_name ?? ticket.lead.legal_name ?? ticket.lead.id}
                  </option>
                )}
                {(leadOptions ?? [])
                  .filter((lead: Lead) => lead.id !== ticket.lead?.id)
                  .map((lead: Lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.dba_name ?? lead.legal_name ?? lead.id}
                    </option>
                  ))}
              </select>
            )}
          </div>

          <div className="rounded-md border border-gray-200 p-3">
            <div className="text-sm font-medium text-gray-700">SLA</div>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {[
                ['1_day', 'Respond within 1 day'],
                ['2_3_days', 'Respond within 3 days'],
                ['7_days', 'Respond within 7 days'],
                ['beginning_of_next_month', `Beginning of next month (${eomPreview})`],
                ['custom', 'Custom days'],
              ].map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    checked={slaType === value}
                    onChange={() => setSlaType(value as TicketSlaType)}
                  />
                  {label}
                </label>
              ))}
            </div>
            {slaType === 'custom' && (
              <label className="mt-3 block text-sm font-medium text-gray-700">
                Custom days
                <input
                  type="number"
                  min={1}
                  max={45}
                  className="mt-1 block w-32 rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={slaCustomDays}
                  onChange={(event) => setSlaCustomDays(Number(event.target.value))}
                />
              </label>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => updateMutation.mutate()} disabled={!canSubmit}>
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}
